import axios from 'axios';
import config from './config';
import { goToLogin } from './navigation';
import { getAuthToken } from './helpers';

// Constants
// Use relative URLs in production (config.API_BASE_URL = '/api')
const API_BASE_URL = config.API_BASE_URL;

/**
 * Available Analysis Modes - Updated January 13, 2026
 * Internal: All modes use optimized model configuration
 * Note: Model names are internal only - not exposed to frontend UI
 */
const DEFAULT_MODEL = "gemini-2.5-flash";
const AVAILABLE_MODELS = [
  // Display-friendly names without model branding
  { id: "gemini-2.5-flash", name: "Standard", description: "Optimized analysis (Default)", isDefault: true },
  { id: "gemini-3-pro-preview", name: "Advanced", description: "Deep reasoning analysis", isDefault: false },
];
const DEFAULT_MAX_TOKENS = 8096;
const GOOGLE_API_TIMEOUT = 60000; // 60 seconds timeout

// Initialize with default values
let googleApiKey = '';
let googleAiClient = null;
let fileManager = null;
let filesInSession = [];

/**
 * Initialize the Google Generative AI client
 * This should be called once when the component mounts
 */
export const initGoogleAI = async () => {
  try {
    // Try to get the token from the server
    const response = await axios.get(`${API_BASE_URL}/coach/get-gemini-token`, {
      timeout: 10000,
      headers: {
        Authorization: getAuthToken(),
      },
    });

    if (response.data && response.data.token) {
      googleApiKey = response.data.token;
      console.log('Google API token received successfully');
      return true;
    } else {
      console.error('Failed to get Google API token - invalid response format');
      return false;
    }
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('Failed to initialize Google AI client:', error);
    }
    return false;
  }
}

/**
 * Process a file for use with Google Generative AI API
 * 
 * @param {File} file - The file object to process
 * @returns {Promise<Object>} - The processed file object with content
 */
export const uploadFileToGoogleAI = async (file) => {
  // If the file is already in the session, return it
  const existingFile = filesInSession.find(f => f.originalFile.name === file.name);
  if (existingFile) {
    console.log(`File ${file.name} already processed and cached`);
    return existingFile.file;
  }

  try {
    // Send the file to our server endpoint for processing
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API_BASE_URL}/chat/upload-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: getAuthToken(),
      },
      timeout: 30000 // 30 second timeout for file uploads
    });

    if (response.data && response.data.file) {
      // Store in session for reuse
      filesInSession.push({
        originalFile: file,
        file: response.data.file
      });
      
      console.log(`File processed successfully: ${file.name}`);
      return response.data.file;
    } else {
      throw new Error('Invalid response from file processing endpoint');
    }
  } catch (error) {
    console.error(`Error uploading file ${file.name}:`, error);
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};

/**
 * Wait for files to be processed by Google AI
 * (This is a simplified version since we're handling files directly on the client)
 * 
 * @param {Array} files - Array of file objects returned by Google AI
 * @returns {Promise<Boolean>} - True if all files are active
 */
export const waitForFilesToBeActive = async (files) => {
  if (!files || files.length === 0) return true;
  
  try {
    // In our simplified approach, files are immediately available
    // But we still call the endpoint for consistency
    const response = await axios.post(`${API_BASE_URL}/chat/wait-for-files`, {
      fileNames: files.map(file => file.name)
    }, {
      timeout: 5000 // 5 second timeout (should be quick)
    });
    
    return true;
  } catch (error) {
    console.error('Error in file readiness check:', error);
    // Return true anyway since we're not actually depending on this step
    return true;
  }
};

/**
 * Send a message to the Google Generative AI API
 * 
 * @param {Array} messages - Array of message objects {role, content}
 * @param {Array} files - Array of file objects for context
 * @param {Object} options - Additional options
 * @returns {Promise<String>} - The response from the API
 * 
 * NOTE: This function has been heavily instrumented with logs to debug communication issues.
 * If text input is not being sent correctly, check the browser console and server logs.
 */
export const sendMessageToGoogleAI = async (messages, files = [], options = {}) => {
  try {
    // Configure options with defaults
    const requestOptions = {
      model: options.model || DEFAULT_MODEL,
      maxTokens: options.maxTokens || DEFAULT_MAX_TOKENS,
      temperature: options.temperature || 0.7,
      // Sport and team context for system prompt customization
      sport: options.sport || null,
      teamId: options.teamId || null,
      teamName: options.teamName || null,
      language: options.language || 'en',
    };

    // Debug log the messages we're sending
    console.log('Messages to send:', JSON.stringify(messages.map(msg => ({
      role: msg.role,
      contentLength: msg.content ? msg.content.length : 0,
      contentPreview: msg.content ? msg.content.substring(0, 50) + '...' : 'empty'
    })), null, 2));

    // Format files for the API
    let filesData = [];
    if (files && files.length > 0) {
      filesData = files.map(file => ({
        name: file.name,
        displayName: file.displayName,
        mimeType: file.mimeType,
        uri: file.uri,
        content: file.content // Include the file content for our simplified approach
      }));
    }

    // Prepare last message parts with files
    const lastMessage = messages[messages.length - 1];
    
    // Validate the last message
    if (!lastMessage || typeof lastMessage !== 'object') {
      console.error('Invalid last message:', lastMessage);
      throw new Error('Invalid message format: Last message is missing or invalid');
    }
    
    console.log('Last message content:', lastMessage.content ? 'Content exists' : 'No content');
    
    const lastMessageWithFiles = {
      role: lastMessage.role || 'user',  // Default to user if role is missing
      parts: []
    };

    // Add text content if it exists
    if (lastMessage.content) {
      lastMessageWithFiles.parts.push({ text: lastMessage.content });
      console.log('Added text part to message');
    } else {
      // Ensure we have content
      lastMessageWithFiles.parts.push({ text: "Please provide a helpful response." });
      console.log('Added default text part to message');
    }

    // Add files to the message parts
    if (filesData.length > 0) {
      filesData.forEach(file => {
        // Include file data with our approach
        lastMessageWithFiles.parts.push({
          fileData: {
            mimeType: file.mimeType || 'application/octet-stream',
            fileUri: file.uri || '',
            displayName: file.displayName || 'file',
            content: file.content || ''
          }
        });
      });
      console.log(`Added ${filesData.length} files to message parts`);
    }

    // Replace the last message with the enhanced version
    const enhancedMessages = [...messages.slice(0, -1), lastMessageWithFiles];
    
    // Log the enhanced messages structure (for debugging)
    console.log('Enhanced messages structure:', JSON.stringify(enhancedMessages.map(msg => ({
      role: msg.role,
      hasParts: msg.parts ? msg.parts.length > 0 : false,
      partsCount: msg.parts ? msg.parts.length : 0
    })), null, 2));

    // Make the request to the server endpoint
    console.log('Sending request to server with enhancedMessages');
    
    // Ensure we have a valid message structure
    if (!lastMessageWithFiles.parts || lastMessageWithFiles.parts.length === 0) {
      // Add a default text part if none exists
      lastMessageWithFiles.parts = [{ text: lastMessage.content || "Please provide a helpful response." }];
      console.log('Added fallback text part to message because parts were empty');
    }
    
    // Log the full request to help debug
    console.log('Sending request to:', `${API_BASE_URL}/chat/google`);
    
    // Log exact final request payload for debugging
    console.log('======= FINAL REQUEST TO SERVER =======');
    const payload = {
      messages: enhancedMessages,
      options: requestOptions
    };
    console.log(JSON.stringify(payload, null, 2));
    console.log('=======================================');
    
    const response = await axios.post(`${API_BASE_URL}/chat/google`, payload, {
      timeout: GOOGLE_API_TIMEOUT,
      headers: {
        Authorization: getAuthToken(),
      },
    });
    // Check response format
    if (response.data && response.data.content) {
      console.log('Received successful response from server');
      return response.data.content;
    } else if (response.data && response.data.message) {
      // Server returned an error message
      console.error('Server returned error:', response.data.message);
      throw new Error(response.data.message || 'Server error');
    } else {
      console.error('Invalid response format:', response.data);
      throw new Error('Invalid response format from Google AI API');
    }
  } catch (error) {
    console.error('Error calling Google AI API:', error);
    throw new Error(`Failed to get response: ${error.message}`);
  }
};

/**
 * Generate a fallback response when the API is unavailable
 * 
 * @param {String} query - The user's query
 * @returns {String} - A fallback response
 */
export const generateFallbackResponse = (query) => {
  const lowerQuery = query.toLowerCase();
  
  if (lowerQuery.includes('analyze') || lowerQuery.includes('data') || lowerQuery.includes('file')) {
    return "I'm sorry, but I'm unable to analyze the data at the moment as I can't access the Google Generative AI service. Please check your connection and try again later.";
  }
  
  if (lowerQuery.includes('help') || lowerQuery.includes('how to')) {
    return "I'm here to help analyze data and provide insights, but I'm currently in offline mode. To use my full capabilities, please ensure you have a working internet connection and API access.";
  }
  
  // Default fallback response
  return "I'm currently operating in offline mode and cannot process your request. Please check your internet connection or API configuration and try again.";
};

/**
 * Clear all uploaded files from the session
 */
export const clearUploadedFiles = () => {
  filesInSession = [];
};

export { AVAILABLE_MODELS };

export default {
  initGoogleAI,
  uploadFileToGoogleAI,
  waitForFilesToBeActive,
  sendMessageToGoogleAI,
  generateFallbackResponse,
  clearUploadedFiles,
  AVAILABLE_MODELS
};